import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import Service from "../api/service";
import "./AssigneePopup.css";
import { getTeamProjectMembers, getTeamTask, updateTeamTask } from "../utility/teamProjectService";
import { showSuccessToast, showErrorToast, showInfoToast } from "./toasts/ToastNotification";
import {
  getCachedProjectMembers,
  setCachedProjectMembers,
  getCachedGlobalUsers,
  setCachedGlobalUsers
} from "../utility/assigneeCache";

const AssigneePopup = ({ taskId, projectId, taskName, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [potentialAssignees, setPotentialAssignees] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [initialIds, setInitialIds] = useState(new Set());
  // Maps login → original P&O object ID (used when sending API calls to remove assignees)
  const loginToObjIdRef = React.useRef({});
  // Maps login → Full Name (used to ensure UI updates with names, not logins)
  const nameMapRef = React.useRef({});
  const searchInputRef = React.useRef(null);

  // Lock background scroll when popup is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // Focus search on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const enoviaService = Service.EnoviaService();
        const isTeamProj = projectId && (projectId.includes("uuid:") || projectId.startsWith("uuid:"));

        // 1. Fetch current task assignees (NEVER CACHED, task-specific)
        let currentData = [];
        const relMap = {};
        const loginToObjId = {};
        const nameMap = {};

        if (isTeamProj) {
          try {
            const entity = await getTeamTask(taskId);
            if (entity && entity.hasAssignee && Array.isArray(entity.hasAssignee.member)) {
              currentData = entity.hasAssignee.member.map(m => {
                let cleanId = m.identifier || "";
                if (cleanId.startsWith("<") && cleanId.endsWith(">")) {
                  cleanId = cleanId.substring(1, cleanId.length - 1);
                }
                return {
                  id: cleanId,
                  login: m.accountName,
                  dataelements: {
                    name: m.accountName,
                    login: m.accountName,
                    fullname: m.accountName
                  }
                };
              });
            }
          } catch (e) {
            console.error("Failed to load Team Project task assignees:", e);
          }
        } else {
          const currentRes = await enoviaService.getTaskAssignees(taskId);
          currentData = currentRes?.data?.data || [];
        }

        currentData.forEach(a => {
          const de = a.dataelements || {};
          const login = de.name || de.login || a.login || a.id;
          const fullName = de.fullname || de.name || login;

          const relId = a.relId;
          if (relId) relMap[login] = relId;
          loginToObjId[login] = a.id;
          nameMap[login] = fullName;
        });

        // 2. Fetch project members (CACHED per projectId)
        if (projectId) {
          const cachedProjectData = getCachedProjectMembers(projectId);

          if (cachedProjectData) {
            // Merge cached data — do NOT overwrite task-specific IDs already resolved above
            Object.entries(cachedProjectData.loginToObjId).forEach(([k, v]) => {
              if (!loginToObjId[k]) loginToObjId[k] = v;
            });
            Object.entries(cachedProjectData.nameMap).forEach(([k, v]) => {
              if (!nameMap[k]) nameMap[k] = v;
            });
          } else {
            const pLoginToObjId = {};
            const pNameMap = {};

            if (isTeamProj) {
              try {
                const membersRes = await getTeamProjectMembers(projectId);
                const projectMembers = Array.isArray(membersRes?.data) ? membersRes.data : (membersRes?.member || membersRes?.data?.member || []);

                projectMembers.forEach(m => {
                  const login = m.accountName || "";
                  let cleanId = m.identifier || "";
                  if (cleanId.startsWith("<") && cleanId.endsWith(">")) {
                    cleanId = cleanId.substring(1, cleanId.length - 1);
                  }
                  if (login) {
                    pLoginToObjId[login] = cleanId;
                    pNameMap[login] = login;

                    if (!loginToObjId[login]) loginToObjId[login] = cleanId;
                    if (!nameMap[login]) nameMap[login] = login;
                  }
                });
              } catch (e) {
                console.error("Failed to load Team Project members:", e);
              }
            } else {
              const projectRes = await enoviaService.getProjectMembers(projectId);
              const projectObj = projectRes?.data?.data?.[0];
              const projectMembers = projectObj?.relateddata?.members || [];

              projectMembers.forEach(m => {
                const de = m.dataelements || {};
                const login = de.name || de.login || m.id;
                const fullName = de.fullname || de.name || login;

                if (login) {
                  pLoginToObjId[login] = m.id;
                  pNameMap[login] = fullName;

                  // Update local maps
                  if (!loginToObjId[login]) loginToObjId[login] = m.id;
                  if (!nameMap[login]) nameMap[login] = fullName;
                }
              });
            }

            setCachedProjectMembers(projectId, {
              loginToObjId: pLoginToObjId,
              nameMap: pNameMap
            });
          }
        }

        loginToObjIdRef.current = loginToObjId;
        nameMapRef.current = nameMap;

        const currentLogins = Object.keys(loginToObjId).filter(l =>
          currentData.some(a => (a.dataelements?.name || a.dataelements?.login || a.login || a.id) === l)
        );

        setSelectedIds(new Set(currentLogins));
        setInitialIds(new Set(currentLogins));

        // 3. Populate Potential Assignees
        const pLogins = Object.keys(loginToObjId);
        if (projectId && pLogins.length > 0) {
          const projectAssignees = pLogins.map(login => {
            const fullName = nameMap[login] || login;
            return {
              id: login,
              name: fullName,
              username: login,
              initials: getInitials(fullName)
            };
          });
          // For project spaces, sort alphabetically by full name
          projectAssignees.sort((a, b) => a.name.localeCompare(b.name));
          setPotentialAssignees(projectAssignees);
        } else {
          // Fallback: Fetch ALL system users (CACHED globally)
          const cachedGlobalData = getCachedGlobalUsers();
          if (cachedGlobalData) {
            // Merge cached names
            Object.assign(nameMapRef.current, cachedGlobalData.nameMap);
            setPotentialAssignees(cachedGlobalData.users);
          } else {
            const PAGE_LIMIT = 100;
            const pageRes = await enoviaService.getAllUsers(0, PAGE_LIMIT);
            const pageData = pageRes?.data?.data || pageRes?.data?.results || pageRes?.data || [];
            const allUsers = Array.isArray(pageData) ? pageData : [];

            const gNameMap = {};
            const formattedMembers = allUsers.map(u => {
              const firstName = u.firstname?.trim() || "";
              const lastName = u.lastname?.trim() || "";
              const fullName = u.fullname
                || (firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName)
                || u.name
                || u.login
                || u.id
                || "Unknown";

              const canonicalId = u.login || u.uid || u.id;
              gNameMap[canonicalId] = fullName;
              nameMapRef.current[canonicalId] = fullName;

              return {
                id: canonicalId,
                name: fullName,
                username: u.login || "",
                email: u.email || "",
                initials: getInitials(fullName)
              };
            });

            setCachedGlobalUsers({
              users: formattedMembers,
              nameMap: gNameMap
            });
            setPotentialAssignees(formattedMembers);
          }
        }
      } catch (err) {
        console.error("Failed to fetch assignee data:", err);
        showErrorToast("Failed to load assignees.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [taskId, projectId]);

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase();
  };

  // Handle Search
  useEffect(() => {
    if (!searchQuery.trim()) return;

    const delayDebounceFn = setTimeout(async () => {
      try {
        const enoviaService = Service.EnoviaService();
        const searchRes = await enoviaService.searchUsers(searchQuery);
        const searchData = searchRes?.data?.data || searchRes?.data?.results || [];

        const formattedSearch = searchData.map(u => {
          const firstName = u.firstname || "";
          const lastName = u.lastname || "";
          const fullName = (firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName)
            || u.fullname || u.name || u.login || u.id || "Unknown";
          const canonicalId = u.login || u.uid || u.id;

          // Update name map for search results too
          nameMapRef.current[canonicalId] = fullName;

          return {
            id: canonicalId,
            name: fullName,
            username: u.login || "",
            initials: getInitials(fullName)
          };
        });

        setPotentialAssignees(prev => {
          const combined = [...prev];
          formattedSearch.forEach(s => {
            if (!combined.find(p => p.id === s.id)) {
              combined.push(s);
            }
          });
          return combined;
        });
      } catch (err) {
        console.error("Search failed:", err);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const toggleAssignee = (e, id) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);

    const isTeamProj = projectId && (projectId.includes("uuid:") || projectId.startsWith("uuid:"));

    if (isTeamProj) {
      try {
        const addedLogins = [...selectedIds].filter(id => !initialIds.has(id));
        const removedLogins = [...initialIds].filter(id => !selectedIds.has(id));

        const assigneeOperations = [];

        addedLogins.forEach(login => {
          assigneeOperations.push({
            operation: "add",
            identifier: login
          });
        });

        removedLogins.forEach(login => {
          assigneeOperations.push({
            operation: "remove",
            identifier: login
          });
        });

        if (assigneeOperations.length > 0) {
          await updateTeamTask(taskId, {
            hasAssignee: assigneeOperations
          });
          showSuccessToast("Assignees updated successfully!");
        } else {
          showInfoToast("No changes were made.");
        }

        if (onUpdate) {
          const updatedNames = [...selectedIds].map(id => nameMapRef.current[id] || id);
          onUpdate(updatedNames);
        }
      } catch (err) {
        console.error("Failed to update Team Project assignees:", err);
        showErrorToast(`Failed to update assignees: ${err.message || "Connection error"}`);
      } finally {
        setSaving(false);
        onClose();
      }
      return;
    }

    const enoviaService = Service.EnoviaService();
    const addedLogins = [...selectedIds].filter(id => !initialIds.has(id));
    const removedLogins = [...initialIds].filter(id => !selectedIds.has(id));
    const addedObjIds = addedLogins.map(login => loginToObjIdRef.current[login]).filter(Boolean);
    const removedObjIds = removedLogins.map(login => loginToObjIdRef.current[login]).filter(Boolean);
    const unresolvedAdded = addedLogins.filter(login => !loginToObjIdRef.current[login]);

    let removeSuccess = true;
    let addSuccess = true;
    let removeError = null;
    let addError = null;

    // 1. Remove old assignees — wait for result
    if (removedObjIds.length > 0) {
      try {
        await enoviaService.removeTaskAssignees(taskId, removedObjIds);
      } catch (err) {
        removeSuccess = false;
        removeError = err.response?.data?.error || err.message || "Connection error";
        console.error("Failed to remove assignees:", err);
      }
    }

    // 2. Add new assignees — wait for result
    if (addedObjIds.length > 0) {
      try {
        await enoviaService.addTaskAssignees(taskId, addedObjIds);
      } catch (err) {
        addSuccess = false;
        addError = err.response?.data?.error || err.message || "Connection error";
        console.error("Failed to add assignees:", err);
      }
    }

    // 3. Show toasts ONLY after all API calls are done
    if (!removeSuccess) {
      showErrorToast(`Failed to remove assignee(s): ${removeError}`, { autoClose: 6000 });
    }
    if (!addSuccess) {
      showErrorToast(`Failed to add assignee(s): ${addError}`, { autoClose: 6000 });
    }
    if (unresolvedAdded.length > 0) {
      showErrorToast(`${unresolvedAdded.length} user(s) skipped — must be project members to be assigned.`, { autoClose: 6000 });
      console.warn("Some added assignees could not be resolved to Oids:", unresolvedAdded);
    }

    const anythingAttempted = removedObjIds.length > 0 || addedObjIds.length > 0;
    if (anythingAttempted && removeSuccess && addSuccess) {
      showSuccessToast("Assignees updated successfully!");
    } else if (!anythingAttempted && unresolvedAdded.length === 0) {
      showInfoToast("No changes were made.");
    }

    // 4. Update UI — only reflect what actually succeeded
    if (onUpdate) {
      const finalizedIds = new Set([...initialIds]);

      if (removeSuccess) {
        removedLogins.forEach(login => {
          if (loginToObjIdRef.current[login]) finalizedIds.delete(login);
        });
      }
      if (addSuccess) {
        addedLogins.forEach(login => {
          if (loginToObjIdRef.current[login]) finalizedIds.add(login);
        });
      }

      const updatedNames = [...finalizedIds].map(id => nameMapRef.current[id] || id);
      onUpdate(updatedNames);
    }

    setSaving(false);
    onClose();
  };

  const filteredList = potentialAssignees.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return createPortal(
    <div
      className="assignee-popup-overlay"
      onClick={onClose}
      onWheel={(e) => e.stopPropagation()}
    >
      <div className="assignee-popup-container" onClick={e => e.stopPropagation()}>
        <div className="assignee-popup-header">
          <h3>Manage Assignees {taskName ? `for ${taskName}` : ""}</h3>
          <button className="assignee-popup-close" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="assignee-popup-search">
          <div className="assignee-search-input-wrapper">
            <SearchIcon className="assignee-search-icon" fontSize="small" />
            <input
              ref={searchInputRef}
              type="text"
              className="assignee-search-input"
              placeholder="Search people..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="assignee-list">
          {loading ? (
            <div className="loading-container">Loading members...</div>
          ) : filteredList.length > 0 ? (
            filteredList.map(person => (
              <div
                key={person.id}
                className="assignee-item"
                onClick={(e) => toggleAssignee(e, person.id)}
              >
                <input
                  type="checkbox"
                  className="assignee-item-checkbox"
                  checked={selectedIds.has(person.id)}
                  readOnly
                />
                <div className="assignee-item-info">
                  <div className="assignee-avatar">
                    {person.initials}
                  </div>
                  <div>
                    <span className="assignee-name">{person.name}</span>
                    <span className="assignee-email">{person.email}</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-results">No people found.</div>
          )}
        </div>

        <div className="assignee-popup-footer">
          <button className="btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? "Saving..." : "Apply Changes"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AssigneePopup;
